import { doodBlockConfig, propertyValue } from './dood-block-config';
import {
  buildOrderedBlockDescriptor,
  type DescriptorFieldOrderItem,
} from './descriptor-field-order';
import type { IBlockDescriptor } from './descriptor';

export type CartFunnelMinimumAgeItemBlockScenarioContent = {
  answer: 'unanswered' | 'no';
};

export const CARTFUNNELMINIMUMAGEITEMBLOCK_LEGACY_FIELD_ORDER = [
  "question",
  "minimum_age",
  "yes_label",
  "no_label",
  "denial_message",
  "message_color",
  { type: 'separator' },
  "text_color",
  "button_font_size",
  "button_radius",
  "choice_border_color",
  "choice_text_color",
  "choice_background_color",
  "selected_text_color",
  "selected_background_color"
] as const satisfies readonly DescriptorFieldOrderItem[];

const {
  cartFunnelMinimumAgeItemBlockDescriptor: _generatedCartFunnelMinimumAgeItemBlockDescriptor,
  cartFunnelMinimumAgeItemBlockBlocDefinition,
  cartFunnelMinimumAgeItemBlockDashboardLayout,
  cartFunnelMinimumAgeItemBlockConfig,
  cartFunnelMinimumAgeItemBlockResolveConfig,
  cartFunnelMinimumAgeItemBlockProperties,
  cartFunnelMinimumAgeItemBlockDefinitionSetupScenarios,
} = doodBlockConfig(
  {
    name: "Cart funnel / Minimum age",
    componentType: 'CartFunnelMinimumAgeItemBlockComponent',
    category: 'cart',
    description: "Cart funnel / Minimum age.",
    keywords: ["age", "yes", "no"],
    allowedIn: [
      { bloc: "CartFunnelFormBlockComponent", slot: "items" },
      { bloc: "KioskCartFunnelFormBlockComponent", slot: "items" },
    ],
    sharedFields: {}
  },

  () => ({
    question: propertyValue({
      type: 'translation',
      value: '',
      options: {
        label: "pages.blocks.cart-funnel-minimum-age.question.label",
        help: "pages.blocks.cart-funnel-minimum-age.question.help",
      }
    }),
    minimum_age: propertyValue({
      type: 'number',
      value: 18,
      options: {
        label: "pages.blocks.cart-funnel-minimum-age.minimum-age.label",
        help: "pages.blocks.cart-funnel-minimum-age.minimum-age.help",
        default: 18,
        min: 0,
        max: 120,
        max_length: 120,
      }
    }),
    yes_label: propertyValue({
      type: 'translation',
      value: '',
      options: {
        label: "pages.blocks.cart-funnel-minimum-age.yes-label.label",
      }
    }),
    no_label: propertyValue({
      type: 'translation',
      value: '',
      options: {
        label: "pages.blocks.cart-funnel-minimum-age.no-label.label",
      }
    }),
    denial_message: propertyValue({
      type: 'translation',
      value: '',
      options: {
        label: "pages.blocks.cart-funnel-minimum-age.denial-message.label",
        help: "pages.blocks.cart-funnel-minimum-age.denial-message.help",
      }
    }),
    button_font_size: propertyValue({
      type: 'number',
      value: 48,
      options: {
        label: "pages.blocks.cart-funnel-minimum-age.button-font-size.label",
        help: "pages.blocks.cart-funnel-minimum-age.button-font-size.help",
        default: 48,
        min: 12,
        max: 160,
        max_length: 160,
      }
    }),
    button_radius: propertyValue({
      type: 'number',
      value: 16,
      options: {
        label: "pages.blocks.cart-funnel-minimum-age.button-radius.label",
        help: "pages.blocks.cart-funnel-minimum-age.button-radius.help",
        default: 16,
        min: 0,
        max: 200,
        max_length: 200,
      }
    }),
    choice_border_color: propertyValue({
      type: 'color',
      value: {"type":"palette","value":"neutral-800"},
      options: {
        label: "pages.blocks.cart-funnel-minimum-age.choice-border-color.label",
      }
    }),
    text_color: propertyValue({
      type: 'color',
      value: {"type":"palette","value":"neutral-800"},
      options: {
        label: "pages.blocks.cart-funnel-minimum-age.text-color.label",
      }
    }),
    choice_text_color: propertyValue({
      type: 'color',
      value: {"type":"palette","value":"neutral-800"},
      options: {
        label: "pages.blocks.cart-funnel-minimum-age.choice-text-color.label",
      }
    }),
    choice_background_color: propertyValue({
      type: 'color',
      value: {"type":"palette","value":"neutral-50"},
      options: {
        label: "pages.blocks.cart-funnel-minimum-age.choice-background-color.label",
      }
    }),
    selected_text_color: propertyValue({
      type: 'color',
      value: {"type":"palette","value":"neutral-50"},
      options: {
        label: "pages.blocks.cart-funnel-minimum-age.selected-text-color.label",
      }
    }),
    selected_background_color: propertyValue({
      type: 'color',
      value: {"type":"palette","value":"primary-600"},
      options: {
        label: "pages.blocks.cart-funnel-minimum-age.selected-background-color.label",
      }
    }),
    message_color: propertyValue({
      type: 'color',
      value: {"type":"palette","value":"error-700"},
      options: {
        label: "pages.blocks.cart-funnel-minimum-age.message-color.label",
      }
    })
  }),

  () => ({
    tabs: [
      {
        label: "pages.blocks.cart-funnel-minimum-age.tab.content.label",
        fieldsets: [
          {
            legend: "pages.blocks.cart-funnel-minimum-age.fieldset.question.label",
            fields: ["question", "minimum_age", "text_color"],
          },
          {
            legend: "pages.blocks.cart-funnel-minimum-age.fieldset.choices.label",
            fields: ["no_label", "yes_label"],
          },
          {
            legend: "pages.blocks.cart-funnel-minimum-age.fieldset.denial.label",
            fields: ["denial_message", "message_color"],
          },
          {
            legend: "pages.blocks.cart-funnel-minimum-age.fieldset.buttons.label",
            fields: [
              "button_font_size",
              "button_radius",
              "choice_border_color",
              "choice_text_color",
              "choice_background_color",
              "selected_text_color",
              "selected_background_color",
            ],
          },
        ],
      },
    ],
  })
);

export function cartFunnelMinimumAgeItemBlockDescriptor(): IBlockDescriptor;
export function cartFunnelMinimumAgeItemBlockDescriptor(
  fieldOrder: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor;
export function cartFunnelMinimumAgeItemBlockDescriptor(
  fieldOrder?: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor {
  if (fieldOrder === undefined) {
    return _generatedCartFunnelMinimumAgeItemBlockDescriptor;
  }
  return buildOrderedBlockDescriptor(_generatedCartFunnelMinimumAgeItemBlockDescriptor, cartFunnelMinimumAgeItemBlockProperties, fieldOrder);
}

export {
  cartFunnelMinimumAgeItemBlockBlocDefinition,
  cartFunnelMinimumAgeItemBlockDashboardLayout,
  cartFunnelMinimumAgeItemBlockProperties,
  cartFunnelMinimumAgeItemBlockConfig,
  cartFunnelMinimumAgeItemBlockResolveConfig,
  cartFunnelMinimumAgeItemBlockDefinitionSetupScenarios,
};

export type CartFunnelMinimumAgeItemBlockConfig = typeof cartFunnelMinimumAgeItemBlockConfig;
